import { useQuery } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "~/shared/api/client";
import { createMutationHook } from "~/shared/api/query-factories";
import { projectsKeys } from "./api-types";

interface ProjectFile {
    id: string;
    fileName: string;
    originalName: string;
    fileSize: number;
    mimeType: string;
    url: string;
    [key: string]: unknown;
}

async function fetchProjectFiles(projectId: string): Promise<ProjectFile[]> {
    return apiGet<ProjectFile[]>(`/v2/projects/${projectId}/files`);
}

export function useProjectFiles(projectId: string | undefined) {
    return useQuery({
        queryKey: projectsKeys.files(projectId ?? ""),
        queryFn: () => fetchProjectFiles(projectId!),
        enabled: !!projectId,
    });
}

// ---------------------------------------------------------------------------
// File mutation hooks (V2 endpoints, no V1 analogue)
// ---------------------------------------------------------------------------

export const useUploadProjectFile = createMutationHook<
    unknown,
    {
        projectId: string;
        fileName: string;
        originalName: string;
        fileSize: number;
        mimeType: string;
        url: string;
        key?: string;
    }
>({
    mutationFn: ({ projectId, ...body }) => apiPost(`/v2/projects/${projectId}/files`, body),
    invalidate: [projectsKeys.all],
});

export const useDeleteProjectFile = createMutationHook<unknown, { projectId: string; fileId: string }>({
    mutationFn: (input) => apiDelete(`/v2/projects/${input.projectId}/files/${input.fileId}`),
    invalidate: [projectsKeys.all],
});

export const useUpdateProjectFile = createMutationHook<
    unknown,
    { projectId: string; fileId: string; fileName?: string; comment?: string }
>({
    mutationFn: ({ projectId, fileId, ...body }) =>
        apiPatch(`/v2/projects/${projectId}/files/${fileId}`, body),
    invalidate: (_data, vars) => [projectsKeys.files(vars.projectId)],
});
